import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import Typography from "@material-ui/core/Typography";
import styled from "styled-components/macro";

import Card from "components/Card";
import { SlimSecondaryButton } from "components/Button";
import { Row, Column } from "components/Layout";
import { Chip } from "components/Chip";
import { Button, LinkButton, SecondaryButton } from "components/Form";
import { BusinessFieldWithFilter } from "components/Form/Fields";

import { faSpinnerThird } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  BusinessAction,
  MergeBusinessesAction,
  startTemporaryBusinessResolve,
  updateBusinessOnSuccess,
} from "actions";
import {
  getBusinessRegistrations,
  getBusinesses,
  getEffectiveSaleTitleBySaleId,
  getProperties,
  getSaleById,
  getSideLoadedBusinesses,
  getSpeciesById,
} from "selectors";
import {
  MatchCandidate,
  MatchSource,
  businessToMatchCandidate,
  getBusinessPics,
  getIsResolvingTemporaryBusiness,
  selectUnusedSideLoadedCandidates,
  sideLoadedToMatchCandidate,
} from "selectors/temporaryBusinesses";
import { selectBusinessesInSaleByIdLookup } from "selectors/aggrid/businessesInSale";
import {
  BUSINESS_MATCHED_FIELD_LABELS,
  BusinessMatchedField,
} from "constants/temporaryBusinesses";
import { SaleType } from "constants/sale";
import {
  BusinessFieldFilterOptions,
  DefaultBusinessFieldFilterOptions,
} from "constants/businesses";
import {
  getLivestockSaleId,
  openActivateTemporaryBusinessWithSideLoadedData,
  openEditBusinessModal,
} from "lib/navigation";
import { useHasOrganisationalMergeBusinessPermission } from "hooks";

import { MatchSearchOption } from "./MatchSearchOption";
import {
  useConnectedBusinessMatches,
  useConnectedBusinessSearch,
} from "./useConnectedBusinessMatches";
import { useTemporaryBusinessMatcher } from "./useTemporaryBusinessMatcher";
import { getAUFormattedDate } from "lib/timeFormats";

const MAX_LOT_NUMBERS_SHOWN = 5;
const MAX_PICS_SHOWN = 5;

// Connected Businesses and side-loaded records share the search field with
// Businesses - prefix their option ids so a pick can be told apart.
const OPTION_ID_PREFIXES: Partial<Record<MatchSource, string>> = {
  connected: "connected:",
  sideLoaded: "sideLoaded:",
};

const SOURCE_LABELS: Record<MatchSource, string> = {
  business: "Existing Business",
  connected: "Connected Business",
  sideLoaded: "Legacy Data",
};

function toOptionId({ source, id }: { source: MatchSource; id: string }) {
  return `${OPTION_ID_PREFIXES[source] ?? ""}${id}`;
}

function fromOptionId(optionId: string): { source: MatchSource; id: string } {
  const match = (
    Object.entries(OPTION_ID_PREFIXES) as [MatchSource, string][]
  ).find(([, prefix]) => optionId.startsWith(prefix));
  return match
    ? { source: match[0], id: optionId.slice(match[1].length) }
    : { source: "business", id: optionId };
}

const isSameCandidate = (
  a: { source: MatchSource; id: string } | null,
  b: { source: MatchSource; id: string } | null,
) => Boolean(a && b && a.source === b.source && a.id === b.id);

interface BusinessInSale {
  roleInSale: string[];
  vendorNumbers: string[];
  lotNumbers: string[];
  lotCount: number;
  headCount: number;
}

function formatUsageInSale(usage: BusinessInSale | undefined): string {
  if (!usage?.roleInSale.length) {
    return "Not used in any lots";
  }
  const parts = [usage.roleInSale.join(", ")];
  if (usage.vendorNumbers.length) {
    const vendorNumbers = [...usage.vendorNumbers].sort(
      (a, b) => Number(a) - Number(b),
    );
    parts.push(`Ven. # ${vendorNumbers.join(", ")}`);
  }
  if (usage.lotNumbers.length) {
    const shown = usage.lotNumbers.slice(0, MAX_LOT_NUMBERS_SHOWN).join(", ");
    const hidden = usage.lotNumbers.length - MAX_LOT_NUMBERS_SHOWN;
    const more = hidden > 0 ? ` +${hidden} more` : "";
    parts.push(
      `${usage.lotNumbers.length === 1 ? "Lot" : "Lots"} ${shown}${more}`,
    );
  }
  if (usage.lotCount) {
    parts.push(`${usage.headCount} hd`);
  }
  return parts.join(" · ");
}

const CONNECTED_SEARCH_DELAY_MS = 300;

const RESOLVE_CHANGE_REASON = "Resolved via Temporary Businesses";

interface TemporaryBusinessProps {
  business: Business;
  onActionComplete?: () => void;
  // Reports whether the card has a match, and null once it's gone.
  onMatchedChange?: (businessId: string, isMatched: boolean | null) => void;
  // Called with the Business it's being merged into.
  onMerge?: (target: { id: string; name: string }) => void;
}

const MatchStatusCard = styled(Card)<{ $isMatched: boolean }>`
  margin: ${({ theme }) => `${theme.space[1]}px ${theme.space[2]}px`};
  border-left: 4px solid
    ${({ $isMatched, theme }) =>
      $isMatched ? theme.colors.primary : theme.colors.gold};
`;

const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.space[2]}px;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 220px;
    align-items: stretch;
  }
`;

const SuggestedPanel = styled.div<{ $isPlaceholder: boolean }>`
  border: 1px ${({ $isPlaceholder }) => ($isPlaceholder ? "dashed" : "solid")}
    ${({ theme }) => theme.colors.gray78};
  border-radius: ${({ theme }) => theme.radii[1]}px;
  padding: ${({ theme }) => theme.space[2]}px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[1]}px;
  justify-content: ${({ $isPlaceholder }) =>
    $isPlaceholder ? "center" : "flex-start"};
`;

const MatchSourceLabel = styled(Typography).attrs({
  variant: "overline",
  color: "textSecondary",
})`
  && {
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: 0.04em;
  }
`;

// Keep wrapped names left-aligned.
const SuggestedBusinessLink = styled(LinkButton)`
  text-align: left;
`;

const SearchField = styled.div`
  label:empty {
    display: none;
  }

  && .MuiAutocomplete-inputRoot {
    padding-left: ${({ theme }) => theme.space[2]}px;
    border: 1px solid ${({ theme }) => theme.colors.gray78};
    border-radius: ${({ theme }) => theme.radii[1]}px;
    background: ${({ theme }) => theme.colors.white};
  }

  && .MuiAutocomplete-inputRoot .MuiAutocomplete-input {
    padding: ${({ theme }) => theme.space[1]}px 0;
  }

  && .MuiAutocomplete-input::placeholder {
    color: ${({ theme }) => theme.colors.gray40};
    opacity: 1;
  }

  .MuiInput-underline:before,
  .MuiInput-underline:after {
    display: none;
  }
`;

const ActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[1]}px;
  justify-content: center;

  // Undo the buttons' flex: 1 so they don't stretch in a column.
  > * {
    flex: none;
    white-space: normal;
  }
`;

function DetailChips({
  pics,
  abn,
  email,
}: {
  pics: string[];
  abn?: string | null;
  email?: string | null;
}) {
  return (
    <Row flexWrap>
      {pics.slice(0, MAX_PICS_SHOWN).map(pic => (
        <Chip key={pic}>{`PIC ${pic}`}</Chip>
      ))}
      {pics.length > MAX_PICS_SHOWN && (
        <Chip>{`${pics.length - MAX_PICS_SHOWN} more ${
          pics.length - MAX_PICS_SHOWN === 1 ? "PIC" : "PICs"
        }`}</Chip>
      )}
      {abn && <Chip>{`ABN ${abn}`}</Chip>}
      {email && <Chip>{email}</Chip>}
    </Row>
  );
}

function MatchedFieldChips({ fields }: { fields: BusinessMatchedField[] }) {
  if (!fields.length) {
    return null;
  }
  return (
    <Row flexWrap alignCenter>
      <Typography variant="body2" color="textSecondary">
        Matches:
      </Typography>
      {fields.map(field => (
        <Chip key={field} backgroundColor="successGreen">
          {BUSINESS_MATCHED_FIELD_LABELS[field]}
        </Chip>
      ))}
    </Row>
  );
}

export function TemporaryBusiness({
  business: temporaryBusiness,
  onActionComplete,
  onMatchedChange,
  onMerge,
}: TemporaryBusinessProps) {
  const dispatch = useDispatch();
  const businesses: Record<string, Business> = useSelector(getBusinesses);
  // Merging into another temporary Business wouldn't resolve anything.
  const temporaryBusinessIds = useMemo(
    () =>
      Object.values(businesses)
        .filter(business => business.isTemporary)
        .map(business => business.id),
    [businesses],
  );
  const propertyByIdLookup = useSelector(getProperties);
  const registrationsById = useSelector(getBusinessRegistrations);
  // Bumped on each pick to remount the search field back to its empty state.
  const [searchKey, setSearchKey] = useState(0);
  const [matchOverride, setMatchOverride] = useState<
    { source: MatchSource; id: string } | "cleared" | null
  >(null);

  const sideLoadedBusinesses: Record<string, SideLoadedBusiness> = useSelector(
    getSideLoadedBusinesses,
  );

  const matchTemporaryBusiness = useTemporaryBusinessMatcher();
  const localMatchCandidates = matchTemporaryBusiness(temporaryBusiness);
  const connectedMatchCandidates =
    useConnectedBusinessMatches(temporaryBusiness);
  const matchCandidates = useMemo(
    () => [
      ...localMatchCandidates.filter(
        candidate => candidate.source === "business",
      ),
      ...connectedMatchCandidates,
      ...localMatchCandidates.filter(
        candidate => candidate.source === "sideLoaded",
      ),
    ],
    [localMatchCandidates, connectedMatchCandidates],
  );
  const matchCandidate = matchCandidates[0] ?? null;
  const searchSections = useMemo(
    () =>
      (
        [
          ["Existing Business Matches", "business"],
          ["Connected Business Matches", "connected"],
          ["Legacy Data Matches", "sideLoaded"],
        ] as [string, MatchSource][]
      ).map(([heading, source]) => ({
        heading,
        ids: matchCandidates
          .filter(candidate => candidate.source === source)
          .map(toOptionId),
      })),
    [matchCandidates],
  );

  // What's typed in the search - Connected Businesses are searched on the
  // server, so wait for a pause in typing.
  const [searchText, setSearchText] = useState("");
  const [connectedSearchText, setConnectedSearchText] = useState("");
  useEffect(() => {
    const timeout = setTimeout(
      () => setConnectedSearchText(searchText),
      CONNECTED_SEARCH_DELAY_MS,
    );
    return () => clearTimeout(timeout);
  }, [searchText]);
  const searchedConnected = useConnectedBusinessSearch(connectedSearchText);
  const unusedSideLoaded: MatchCandidate[] = useSelector(
    selectUnusedSideLoadedCandidates,
  );

  // Connected Businesses and legacy records that aren't possible matches, for
  // the sections after Existing Businesses.
  const otherCandidates = useMemo(() => {
    const matchedOptionIds = new Set(matchCandidates.map(toOptionId));
    return [...searchedConnected, ...unusedSideLoaded].filter(
      candidate => !matchedOptionIds.has(toOptionId(candidate)),
    );
  }, [matchCandidates, searchedConnected, unusedSideLoaded]);
  const trailingSearchSections = useMemo(
    () =>
      (
        [
          ["Connected Businesses", "connected"],
          ["Legacy Data", "sideLoaded"],
        ] as [string, MatchSource][]
      ).map(([heading, source]) => ({
        heading,
        ids: otherCandidates
          .filter(candidate => candidate.source === source)
          .map(toOptionId),
      })),
    [otherCandidates],
  );

  // A Connected Business picked from the typed search, kept once the search
  // is cleared.
  const [pickedConnected, setPickedConnected] = useState<MatchCandidate | null>(
    null,
  );

  // Connected Businesses and legacy records join the search list, in their
  // own sections.
  const extraSearchOptions = useMemo(
    () =>
      [...matchCandidates, ...otherCandidates]
        .filter(candidate => candidate.source !== "business")
        .map(candidate => ({
          id: toOptionId(candidate),
          name: candidate.name,
          label: candidate.name,
          abn: candidate.abn,
          primaryContactEmail: candidate.email,
          properties: candidate.pics.map(PIC => ({ PIC })),
          isActive: true,
          isGlobal: false,
          // Legacy records are grouped by the data set they came from.
          subsection:
            candidate.source === "sideLoaded"
              ? sideLoadedBusinesses[candidate.id]?.datasetName ||
                "Unnamed data set"
              : undefined,
        })),
    [matchCandidates, otherCandidates, sideLoadedBusinesses],
  );

  const searchMatchingPics = useMemo(
    () => getBusinessPics(temporaryBusiness, propertyByIdLookup),
    [temporaryBusiness, propertyByIdLookup],
  );
  const renderSearchOption = useCallback(
    option => (
      <MatchSearchOption option={option} matchingPics={searchMatchingPics} />
    ),
    [searchMatchingPics],
  );

  // Picking or clearing only changes the selection; the Use button commits it.
  const manualMatch: MatchCandidate | null = useMemo(() => {
    if (!matchOverride || matchOverride === "cleared") {
      return null;
    }
    const ranked = matchCandidates.find(candidate =>
      isSameCandidate(candidate, matchOverride),
    );
    if (ranked) {
      return ranked;
    }
    if (
      matchOverride.source === "connected" &&
      pickedConnected &&
      isSameCandidate(pickedConnected, matchOverride)
    ) {
      return pickedConnected;
    }
    if (matchOverride.source === "sideLoaded") {
      const record = sideLoadedBusinesses[matchOverride.id];
      return record ? sideLoadedToMatchCandidate(record, []) : null;
    }
    const business = businesses[matchOverride.id];
    return business
      ? businessToMatchCandidate(
          business,
          [],
          propertyByIdLookup,
          registrationsById,
        )
      : null;
  }, [
    matchOverride,
    matchCandidates,
    pickedConnected,
    sideLoadedBusinesses,
    businesses,
    propertyByIdLookup,
    registrationsById,
  ]);
  // Legacy data already used to create a Business (eg for another temporary
  // Business) resolves to that Business, so this one merges into it.
  const toLinkedBusiness = (
    candidate: MatchCandidate | null,
  ): MatchCandidate | null => {
    if (candidate?.source !== "sideLoaded") {
      return candidate;
    }
    const linkedId = sideLoadedBusinesses[candidate.id]?.businessIds.find(
      id => businesses[id] && id !== temporaryBusiness.id,
    );
    return linkedId
      ? businessToMatchCandidate(
          businesses[linkedId],
          candidate.matchedFields,
          propertyByIdLookup,
          registrationsById,
        )
      : candidate;
  };
  const selectedMatch: MatchCandidate | null =
    matchOverride === "cleared"
      ? null
      : toLinkedBusiness(manualMatch ?? matchCandidate);
  const isExistingBusinessMatch = selectedMatch?.source === "business";

  const isMatched = Boolean(selectedMatch);
  const temporaryBusinessId = temporaryBusiness.id;
  useEffect(() => {
    onMatchedChange?.(temporaryBusinessId, isMatched);
  }, [onMatchedChange, temporaryBusinessId, isMatched]);
  useEffect(
    () => () => onMatchedChange?.(temporaryBusinessId, null),
    [onMatchedChange, temporaryBusinessId],
  );

  const otherMatchCount = matchCandidates.filter(
    candidate => !isSameCandidate(candidate, selectedMatch),
  ).length;
  const searchPlaceholder = otherMatchCount
    ? `Search… ${otherMatchCount} ${selectedMatch ? "other " : ""}possible ${
        otherMatchCount === 1 ? "match" : "matches"
      }`
    : "Search…";

  const sale = useSelector(
    getSaleById(temporaryBusiness.addedInLivestockSaleId),
  );
  const saleTitle = useSelector(
    getEffectiveSaleTitleBySaleId(temporaryBusiness.addedInLivestockSaleId),
  );
  const saleSpecies = useSelector(getSpeciesById(sale?.species_id));
  const isClearingSale = sale?.sale_type === SaleType.CLEARING;
  const isInCreatedSale =
    getLivestockSaleId() === temporaryBusiness.addedInLivestockSaleId;
  const hasMergePermission = useHasOrganisationalMergeBusinessPermission();

  const CreateNewButton = isClearingSale ? SecondaryButton : Button;
  const ArchiveButton = isClearingSale ? Button : SecondaryButton;

  const usageInCurrentSale: BusinessInSale | undefined = useSelector(
    selectBusinessesInSaleByIdLookup,
  )[temporaryBusiness.id];
  // Only the current Sale's lots are loaded, so usage can only be described
  // there - elsewhere, point at the Sale the business was created in.
  const usageLine = isInCreatedSale
    ? formatUsageInSale(usageInCurrentSale)
    : sale
      ? `Created in ${[
          saleTitle,
          getAUFormattedDate(new Date(sale.date)),
          sale.sale_type,
          saleSpecies?.name,
          sale.sale_code && `ID ${sale.sale_code}`,
        ]
          .filter(Boolean)
          .join(" · ")}`
      : null;

  const temporaryBusinessPics = useMemo(
    () => getBusinessPics(temporaryBusiness, propertyByIdLookup),
    [temporaryBusiness, propertyByIdLookup],
  );
  // While a resolve request is in flight, the actions are disabled and the
  // clicked one spins.
  const isProcessing = useSelector(
    getIsResolvingTemporaryBusiness(temporaryBusiness.id),
  );
  const [pendingAction, setPendingAction] = useState<
    "use" | "activate" | "inactivate" | null
  >(null);
  const spinnerFor = (action: typeof pendingAction) =>
    isProcessing && pendingAction === action ? (
      <FontAwesomeIcon spin icon={faSpinnerThird} />
    ) : null;

  const resolveWith = (action: unknown) => {
    dispatch(startTemporaryBusinessResolve(temporaryBusiness.id));
    dispatch(action);
    onActionComplete?.();
  };

  const resolveWithMatch = (match: MatchCandidate) => {
    setPendingAction("use");
    if (match.source === "sideLoaded") {
      openActivateTemporaryBusinessWithSideLoadedData(
        temporaryBusiness.id,
        match.id,
      );
    } else if (match.source === "connected") {
      // Add the Connected Business to the user's Businesses, then merge.
      onMerge?.({ id: match.id, name: match.name });
      resolveWith(
        BusinessAction.getOrCreateGlobalBusiness(match.id, {
          mergeSourceBusinessId: temporaryBusiness.id,
          changeReason: RESOLVE_CHANGE_REASON,
        }),
      );
    } else {
      onMerge?.({ id: match.id, name: match.name });
      resolveWith(
        MergeBusinessesAction.create(
          {},
          {
            targetBusinessId: match.id,
            sourceBusinessId: temporaryBusiness.id,
            suppressToast: false,
            suppressErrorToast: false,
            suppressSuccessToast: false,
            changeReason: RESOLVE_CHANGE_REASON,
            saleyardName: null,
          },
        ),
      );
    }
  };

  const handleSaveAsActive = () => {
    setPendingAction("activate");
    resolveWith(
      updateBusinessOnSuccess(
        { id: temporaryBusiness.id, isTemporary: false, isActive: true },
        { changeReason: RESOLVE_CHANGE_REASON },
      ),
    );
  };

  const handleArchive = () => {
    setPendingAction("inactivate");
    resolveWith(
      updateBusinessOnSuccess(
        { id: temporaryBusiness.id, isTemporary: false, isActive: false },
        { changeReason: "Archived via Temporary Businesses" },
      ),
    );
  };

  return (
    <MatchStatusCard fullWidth $isMatched={Boolean(selectedMatch)}>
      <PanelGrid>
        <Column padding={2}>
          <Typography variant="overline" color="textSecondary">
            Temporary
          </Typography>
          <Typography variant="h6">{temporaryBusiness.name}</Typography>
          <DetailChips
            pics={temporaryBusinessPics}
            abn={
              temporaryBusiness.registrationId &&
              registrationsById[temporaryBusiness.registrationId]?.abn
            }
            email={temporaryBusiness.primaryContactEmail}
          />
          {usageLine && (
            <Typography variant="body2" color="textSecondary">
              {usageLine}
            </Typography>
          )}
        </Column>

        <SuggestedPanel $isPlaceholder={!selectedMatch}>
          <Row justifyEnd>
            <Row gridGap={1}>
              {matchOverride &&
                matchCandidate &&
                !isSameCandidate(manualMatch, matchCandidate) && (
                  <SlimSecondaryButton
                    data-tour="restore-suggested-business"
                    onClick={() => setMatchOverride(null)}
                    disabled={isProcessing}
                    type="button"
                  >
                    Restore Suggestion
                  </SlimSecondaryButton>
                )}
              {selectedMatch && (
                <SlimSecondaryButton
                  data-tour="clear-suggested-business"
                  onClick={() => setMatchOverride("cleared")}
                  disabled={isProcessing}
                  type="button"
                >
                  Clear
                </SlimSecondaryButton>
              )}
            </Row>
          </Row>
          {selectedMatch ? (
            <>
              <MatchSourceLabel>
                {SOURCE_LABELS[selectedMatch.source]}
              </MatchSourceLabel>
              <Row alignCenter flexWrap gridGap={1}>
                <Typography variant="h6">
                  {!isExistingBusinessMatch ? (
                    selectedMatch.name
                  ) : (
                    <SuggestedBusinessLink
                      type="button"
                      data-tour="view-suggested-business"
                      onClick={() => openEditBusinessModal(selectedMatch.id)}
                    >
                      {selectedMatch.name}
                    </SuggestedBusinessLink>
                  )}
                </Typography>
                {/* Only the matcher's own suggestion - not a hand-picked one. */}
                {!manualMatch && (
                  <Chip color="black" backgroundColor="successGreen">
                    Auto-matched
                  </Chip>
                )}
              </Row>
              <DetailChips
                pics={selectedMatch.pics}
                abn={selectedMatch.abn}
                email={selectedMatch.email}
              />
              <MatchedFieldChips fields={selectedMatch.matchedFields} />
            </>
          ) : (
            <Typography color="textSecondary">
              No suggested match found. Search for an existing business below,
              or use this business as it is.
            </Typography>
          )}
          <SearchField>
            <Formik
              key={searchKey}
              initialValues={{ existingBusinessId: null }}
              onSubmit={() => {}}
            >
              <Form data-tour="search-existing-businesses">
                <BusinessFieldWithFilter
                  name="existingBusinessId"
                  label=""
                  placeholder={searchPlaceholder}
                  openOnFocus
                  disabled={isProcessing}
                  allowAddNew={false}
                  hideFilters
                  renderOption={renderSearchOption}
                  excludeIds={temporaryBusinessIds}
                  prioritisedSections={searchSections}
                  extraOptions={extraSearchOptions}
                  searchToListAll
                  trailingSections={trailingSearchSections}
                  onInputChange={(_event: unknown, value: string) =>
                    setSearchText(value)
                  }
                  searchFields={[
                    ...DefaultBusinessFieldFilterOptions,
                    BusinessFieldFilterOptions.abn,
                    BusinessFieldFilterOptions.properties,
                  ]}
                  onChangeExtra={(optionId: string | null) => {
                    if (optionId) {
                      setPickedConnected(
                        searchedConnected.find(
                          candidate => toOptionId(candidate) === optionId,
                        ) ?? null,
                      );
                      setMatchOverride(fromOptionId(optionId));
                      setSearchKey(key => key + 1);
                    }
                  }}
                />
              </Form>
            </Formik>
          </SearchField>
        </SuggestedPanel>

        <ActionsColumn>
          {selectedMatch ? (
            <Button
              data-tour="use-existing-business"
              disabled={
                isProcessing ||
                (selectedMatch.source !== "sideLoaded" && !hasMergePermission)
              }
              title={
                selectedMatch.source === "sideLoaded" || hasMergePermission
                  ? undefined
                  : "You don't have permission to merge Businesses"
              }
              onClick={() => resolveWithMatch(selectedMatch)}
            >
              {spinnerFor("use")} Use
            </Button>
          ) : (
            <CreateNewButton
              data-tour="activate-business"
              disabled={isProcessing}
              onClick={handleSaveAsActive}
            >
              {spinnerFor("activate")} Activate
            </CreateNewButton>
          )}
          <ArchiveButton
            data-tour="limit-to-this-sale"
            disabled={isProcessing}
            onClick={handleArchive}
          >
            {spinnerFor("inactivate")} Inactivate
          </ArchiveButton>
        </ActionsColumn>
      </PanelGrid>
    </MatchStatusCard>
  );
}
